"use client"

import { createContext, useContext } from "react"
import type { LocationFeatures } from "@/types/location-features"
import { DEFAULT_FEATURES } from "@/types/location-features"

export type MerchantPlan = "free" | "plus" | "pro" | "nightlife" | "nightlife_pro"

export interface MerchantLocation {
  merchantId: string
  locationId: string
  locationName: string
  locationSlug: string
  timezone: string
  /** PRN-1: business contact info for receipts (locations.address / locations.phone). */
  address: string | null
  phone: string | null
  features: LocationFeatures
  /** reservation_settings.enabled (false si no hay fila). Gatea "Reservas" en el nav. */
  reservationsEnabled: boolean
  /** `catering_settings.enabled` — gatea el item "Catering" del sidebar. */
  cateringEnabled: boolean
  /**
   * I2/I28 (Fase 1b, decisión del coordinador): kill switch de plataforma del
   * riel `shipping` (`GATE_SHIPPING`, ver lib/shipping/gate.ts) — la MITAD
   * server-only del gate doble. `features.shipping` (el toggle del dueño) es
   * la otra mitad. Con cualquiera de las dos apagada, cero diferencia
   * observable: ni la fila "Envíos" en Módulos ni la entrada del sidebar.
   * `process.env.GATE_SHIPPING` no es `NEXT_PUBLIC_*` a propósito (ver la
   * cabecera de lib/shipping/gate.ts) — un cliente no puede leerlo directo,
   * así que viaja server→cliente por ACÁ, resuelto una sola vez en el layout.
   */
  shippingGateOpen: boolean
  vertical: string
  /** Normalized plan: starter|growth → free, pro → pro, nightlife_* → nightlife */
  plan: MerchantPlan
}

const MerchantContext = createContext<MerchantLocation | null>(null)

export function MerchantProvider({
  value,
  children,
}: {
  value: MerchantLocation
  children: React.ReactNode
}) {
  return <MerchantContext.Provider value={value}>{children}</MerchantContext.Provider>
}

export function useLocation(): MerchantLocation {
  const ctx = useContext(MerchantContext)
  if (!ctx) throw new Error("useLocation must be used inside MerchantProvider")
  return ctx
}

export { DEFAULT_FEATURES }
